import KmxArrowDownRightCircleFilled from '@kmx/mui-icons/KmxArrowDownRightCircleFilled';
import KmxInfoCircleStroked from '@kmx/mui-icons/KmxInfoCircleStroked';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography, { type TypographyProps } from '@mui/material/Typography';
import { memo, type ReactNode, useCallback, useMemo, useState } from 'react';

import { TaggingEvent } from '@/context/tagging';
import { formatCurrency } from '@/helpers/formatters';
import { buildCarTitle } from '@/helpers/normalizers';
import useTagging from '@/hooks/useTagging';
import type { ActionsConfig } from '@/types/ActionsConfig';
import type { Car } from '@/types/Car';
import type { CompareConfig } from '@/types/CompareConfig';
import type { CarTileFbsInformation } from '@/types/FinanceHelperTypes';
import type { MenuOptions } from '@/types/MenuOptions';
import type { MonthlyPaymentConfig } from '@/types/MonthlyPaymentConfig';
import type { TileSize } from '@/types/TileSize';

import CompareButton from '../CompareButton/CompareButton';
import FbsChip from '../Fbs/FbsChip';
import FbsModal from '../Fbs/FbsModal';
import Menu from '../Menu/Menu';
import StatusBadge from '../StatusBadge/StatusBadge';
import { Actions, MonthlyPaymentButton, Wrapper } from './Content.styles';

const MONTHLY_PAYMENT_PREFIX = 'Est. ';
const MONTHLY_PAYMENT_SUFFIX = ' mo';

const MILEAGE_SUFFIX = 'K mi';

type ContentProps = {
  car: Car;
  href: string;
  tileSize: TileSize;
  isFeatured: boolean;
  isUnavailable: boolean;
  actions?: ActionsConfig;
  compare?: CompareConfig;
  availability?: ReactNode;
  menuOptions?: MenuOptions;
  monthlyPayment?: MonthlyPaymentConfig;
  fbsInformation?: CarTileFbsInformation;
};

function Content({
  car,
  href,
  actions,
  compare,
  tileSize,
  isFeatured,
  menuOptions,
  availability,
  isUnavailable,
  monthlyPayment,
  fbsInformation,
}: ContentProps) {
  /** Hooks */
  const { dispatchTaggingEvent } = useTagging();

  /** Status */
  const {
    isSold = false,
    isComingSoon = false,
    hasPriceDrop = false,
  } = car.statusInfo;
  const showStatusBadge = tileSize === 'small';

  /** Action Buttons */
  const actionButtonSize = tileSize === 'xlarge' ? 'large' : 'medium';
  const showActions =
    isFeatured &&
    (actions?.primary?.children !== undefined ||
      actions?.secondary?.children !== undefined);

  /** Financing, i.e FBS and Calculator Terms */
  const showInfoIcon = tileSize !== 'small';

  const isFbsLoading = fbsInformation && 'loading' in fbsInformation;

  const formattedFbsMonthlyPayment = useMemo(() => {
    if (!fbsInformation) {
      return null;
    }

    // We need to return something here, even though the text won't be shown.
    // This keeps some of the necessary conditionals as true.
    if (isFbsLoading) {
      return 'loading';
    }

    const { decision, minMonthlyPayment, maxMonthlyPayment } = fbsInformation;

    if (decision !== 'preApproved' && decision !== 'additionalDown') {
      return null;
    }

    if (minMonthlyPayment && maxMonthlyPayment) {
      const payment =
        minMonthlyPayment === maxMonthlyPayment
          ? formatCurrency(minMonthlyPayment)
          : `${formatCurrency(minMonthlyPayment)}-${formatCurrency(maxMonthlyPayment)}`;

      return `${payment}${MONTHLY_PAYMENT_SUFFIX}`;
    }

    return null;
  }, [fbsInformation, isFbsLoading]);

  const formattedCalculatorMonthlyPayment = useMemo(() => {
    //never show calculator info when in fbs mode
    if (fbsInformation) {
      return null;
    }

    if (!monthlyPayment) {
      return null;
    }

    const { amount, maxAmount, minAmount, isEstimated } = monthlyPayment;
    const prefix = isEstimated === true ? MONTHLY_PAYMENT_PREFIX : '';
    let payment;

    if (minAmount !== undefined && maxAmount !== undefined) {
      payment = `${formatCurrency(minAmount)}-${formatCurrency(maxAmount)}`;
    }

    if (amount !== undefined) {
      payment = formatCurrency(amount);
    }

    if (payment) return `${prefix}${payment}${MONTHLY_PAYMENT_SUFFIX}`;

    return null;
  }, [fbsInformation, monthlyPayment]);

  const {
    showMonthlyPayment,
    monthlyPaymentType,
    monthlyPaymentText,
    monthlyPaymentAriaLabel,
  } = useMemo(() => {
    let showMonthlyPayment = false,
      monthlyPaymentType = null,
      monthlyPaymentText = null,
      monthlyPaymentAriaLabel = undefined;

    if (formattedFbsMonthlyPayment || formattedCalculatorMonthlyPayment) {
      showMonthlyPayment = true;
    }

    if (!showMonthlyPayment || tileSize == 'medium' || isUnavailable) {
      return {
        showMonthlyPayment: false,
        monthlyPaymentType,
        monthlyPaymentText,
        monthlyPaymentAriaLabel,
      };
    }

    monthlyPaymentText =
      formattedFbsMonthlyPayment ?? formattedCalculatorMonthlyPayment;

    monthlyPaymentType = formattedFbsMonthlyPayment ? 'fbs' : 'calculator';

    monthlyPaymentAriaLabel =
      monthlyPaymentText?.replace(MONTHLY_PAYMENT_SUFFIX, ' per month') ?? '';

    if (monthlyPaymentText?.includes(MONTHLY_PAYMENT_PREFIX)) {
      monthlyPaymentAriaLabel =
        monthlyPaymentAriaLabel?.replace(
          MONTHLY_PAYMENT_PREFIX,
          'Estimated ',
        ) ?? '';
    }

    return {
      showMonthlyPayment,
      monthlyPaymentType,
      monthlyPaymentText,
      monthlyPaymentAriaLabel,
    };
  }, [
    formattedFbsMonthlyPayment,
    formattedCalculatorMonthlyPayment,
    tileSize,
    isUnavailable,
  ]);

  const [isFbsModalOpen, setIsFbsModalOpen] = useState(false);

  const openFbsModal = useCallback(() => {
    setIsFbsModalOpen(true);

    if (fbsInformation && 'onFbsTermsModalShown' in fbsInformation) {
      fbsInformation.onFbsTermsModalShown?.();
    }
  }, [fbsInformation]);

  const closeFbsModal = () => {
    setIsFbsModalOpen(false);
  };

  const handleInfoButtonClick = useCallback(() => {
    dispatchTaggingEvent({
      event: TaggingEvent.ClickMonthlyPaymentInfoIcon,
      data: { text: 'info icon' },
    });

    if (monthlyPaymentType === 'calculator') {
      monthlyPayment?.onClick?.();
    } else {
      openFbsModal();
    }
  }, [dispatchTaggingEvent, monthlyPayment, monthlyPaymentType, openFbsModal]);

  /** Price */
  const formattedPrice = formatCurrency(car.price);

  const formattedOriginalPrice =
    hasPriceDrop && car.originalPrice
      ? formatCurrency(car.originalPrice)
      : null;

  const showPriceDrop = hasPriceDrop && tileSize !== 'small';

  /** Mileage */
  const mileage = `${Math.ceil(car.mileage / 1000)}`;
  const showMileage = tileSize !== 'small';

  /** Availability */
  const showAvailability =
    availability && tileSize !== 'small' && !isUnavailable;

  /** Compare */
  const showCompareButton =
    compare?.isCompareMode === true && tileSize === 'large' && !isFeatured;

  /** Menu */
  const showMenu =
    menuOptions &&
    menuOptions.length > 0 &&
    tileSize === 'large' &&
    !isFeatured &&
    !showCompareButton;

  /** Year, Make, Model */
  const showSeparateTrim = car.trim && tileSize !== 'small';
  const yearMakeModel = buildCarTitle(car, tileSize === 'small');

  const handleLinkClick = useCallback(() => {
    dispatchTaggingEvent({
      event: TaggingEvent.ClickCarTile,
      data: {
        text: yearMakeModel,
      },
    });
  }, [dispatchTaggingEvent, yearMakeModel]);

  const simulateLinkClick = useCallback(() => {
    dispatchTaggingEvent({
      event: TaggingEvent.ClickCarTile,
      data: {
        text: yearMakeModel,
      },
    });

    window.location.href = href;
  }, [dispatchTaggingEvent, href, yearMakeModel]);

  /** Typography */
  const headingStyles = useMemo(() => {
    const styles: TypographyProps['sx'] = {
      typography: 'body1',
      fontWeight: 700,
    };

    switch (tileSize) {
      case 'small':
      case 'medium':
        styles.typography = 'body2';
        break;
      case 'xlarge':
        styles.typography = 'h5';
        styles.lineHeight = 1.2;
        break;
    }

    return styles;
  }, [tileSize]);

  const priceTextStyles = useMemo(() => {
    const styles: TypographyProps['sx'] = {
      typography: 'body1',
      fontWeight: 700,
    };

    switch (tileSize) {
      case 'small':
        styles.color = 'text.secondary';
        styles.fontWeight = undefined;
        styles.typography = 'caption';
        break;
      case 'medium':
        styles.typography = 'body2';
        break;
    }

    return styles;
  }, [tileSize]);

  const originalPriceTextStyles = useMemo(() => {
    const styles: TypographyProps['sx'] = {
      textDecoration: 'line-through',
      typography: 'body2',
    };

    switch (tileSize) {
      case 'small':
      case 'medium':
        styles.typography = 'caption';
        break;
    }

    return styles;
  }, [tileSize]);

  const textSecondaryStyles = useMemo(() => {
    const styles: TypographyProps['sx'] = {
      typography: 'body2',
      color: 'text.secondary',
    };

    switch (tileSize) {
      case 'medium':
        styles.typography = 'caption';
        break;
    }

    return styles;
  }, [tileSize]);

  return (
    <Wrapper className="kmx-car-tile__content">
      {showMenu ? (
        <Menu options={menuOptions} stockNumber={car.stockNumber} />
      ) : null}

      {showCompareButton ? (
        <CompareButton
          isSelected={compare.isSelected}
          onClick={compare.onClick}
        />
      ) : null}

      {showStatusBadge ? (
        <StatusBadge carStatus={car.statusInfo} size="small" />
      ) : null}

      <Link
        className="kmx-car-tile__make-model-link"
        color="inherit"
        data-ignore-anchor-tag-rule
        href={href}
        onClick={handleLinkClick}
        rel="nofollow"
        sx={{ display: 'block' }}
        underline="none"
      >
        <Typography
          className="kmx-car-tile__year-make-model"
          component="h3"
          noWrap
          sx={headingStyles}
          title={yearMakeModel}
        >
          {yearMakeModel}
        </Typography>

        {showSeparateTrim || showMileage ? (
          <Stack direction="row" spacing={1} useFlexGap>
            {showSeparateTrim ? (
              <Typography
                aria-label={`Trim: ${car.trim}`}
                className="kmx-car-tile__trim"
                component="span"
                noWrap
                sx={textSecondaryStyles}
                title={`Trim: ${car.trim}`}
              >
                {car.trim}
              </Typography>
            ) : null}

            {showSeparateTrim && showMileage ? (
              <Typography component="span" sx={textSecondaryStyles}>
                &middot;
              </Typography>
            ) : null}

            {showMileage ? (
              <Typography
                aria-label={`${mileage},000 miles`}
                className="kmx-car-tile__mileage"
                component="span"
                sx={{ ...textSecondaryStyles, whiteSpace: 'nowrap' }}
              >
                {mileage}
                {MILEAGE_SUFFIX}
              </Typography>
            ) : null}
          </Stack>
        ) : null}
      </Link>

      {showAvailability ? (
        <Typography
          className="kmx-car-tile__availability"
          component="div"
          noWrap
          onClick={simulateLinkClick}
          sx={{ ...textSecondaryStyles, cursor: 'pointer' }}
          title={typeof availability === 'string' ? availability : undefined}
        >
          {availability}
        </Typography>
      ) : null}

      <Stack
        className="kmx-car-tile__pricing"
        direction="row"
        sx={{ columnGap: 1, flexWrap: 'wrap' }}
        useFlexGap
      >
        {showMonthlyPayment ? (
          <>
            {isFbsLoading ? (
              <Skeleton
                variant="rectangular"
                sx={{
                  borderRadius: '0.25rem',
                  height: '26px',
                  width: '35%',

                  '.kmx-car-tile[data-size="small"] &': {
                    height: '20px',
                  },
                }}
              />
            ) : (
              <MonthlyPaymentButton
                data-ignore-anchor-tag-rule
                onClick={handleInfoButtonClick}
                aria-label={monthlyPaymentAriaLabel}
                className="kmx-car-tile__monthly-payment"
              >
                <Typography component="span" sx={priceTextStyles}>
                  {monthlyPaymentText}
                </Typography>
                {showInfoIcon ? (
                  <KmxInfoCircleStroked fontSize="small" />
                ) : null}
              </MonthlyPaymentButton>
            )}

            <Typography
              component="span"
              sx={{
                ...priceTextStyles,
                color: 'text.secondary',
                fontWeight: 400,
              }}
            >
              &middot;
            </Typography>
          </>
        ) : null}

        <Stack
          direction="row"
          onClick={simulateLinkClick}
          spacing={0.5}
          sx={{
            alignItems: 'center',
            cursor: 'pointer',
            flexGrow: 1,
          }}
        >
          {showPriceDrop ? (
            <KmxArrowDownRightCircleFilled
              fontSize="small"
              sx={{ color: 'success.light' }}
            />
          ) : null}

          <Typography
            className="kmx-car-tile__price"
            component="span"
            sx={priceTextStyles}
          >
            {formattedPrice}*
          </Typography>

          {showPriceDrop && formattedOriginalPrice ? (
            <Typography
              aria-label={`Original price: ${formattedOriginalPrice}`}
              className="kmx-car-tile__original-price"
              color="textSecondary"
              component="span"
              sx={originalPriceTextStyles}
            >
              {formattedOriginalPrice}
            </Typography>
          ) : null}
        </Stack>
      </Stack>

      {fbsInformation && 'decision' in fbsInformation ? (
        <>
          <FbsChip
            decision={fbsInformation.decision}
            downPayment={fbsInformation.downPayment}
            conditionAmount={fbsInformation.conditionAmount}
            handleClick={openFbsModal}
            isComingSoon={isComingSoon}
            isSold={isSold}
          />
          <FbsModal
            href={href}
            open={isFbsModalOpen}
            basePrice={car.price}
            handleClose={closeFbsModal}
            fbsInformation={fbsInformation}
          />
        </>
      ) : null}

      {isFbsLoading ? (
        <Skeleton
          variant="rectangular"
          sx={{
            borderRadius: '0.25rem',
            height: '24px',
            marginTop: 1,
            width: '50%',
          }}
        />
      ) : null}

      {showActions ? (
        <Actions className="kmx-car-tile__actions">
          {actions?.primary?.children ? (
            <Button
              className="kmx-car-tile__primary-action"
              size={actionButtonSize}
              {...actions.primary}
            >
              {actions.primary.children}
            </Button>
          ) : null}
          {actions?.secondary?.children ? (
            <Button
              className="kmx-car-tile__secondary-action"
              size={actionButtonSize}
              {...actions.secondary}
            >
              {actions.secondary.children}
            </Button>
          ) : null}
        </Actions>
      ) : null}
    </Wrapper>
  );
}

export default memo(Content);
